import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
} from "@material-ui/icons";

const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "svg"];
const VIDEO_EXTS = ["mp4", "webm", "ogg", "mov"];

function getFileCategory(fileName, fileType) {
  const ext = fileName?.split(".").pop()?.toLowerCase();
  if (
    (fileType && fileType.startsWith("image/")) ||
    IMAGE_EXTS.includes(ext)
  ) {
    return "image";
  }
  if (
    (fileType && fileType.startsWith("video/")) ||
    VIDEO_EXTS.includes(ext)
  ) {
    return "video";
  }
  return "other";
}

const AttachmentPreviewDialog = ({
  open,
  onClose,
  fileName,
  fileType,
  downloadUrl,
}) => {
  if (!open) return null;

  const category = getFileCategory(fileName, fileType);

  const handleDownload = () => {
    if (downloadUrl) {
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = fileName || "download";
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxWidth: "90vw",
          maxHeight: "90vh",
          overflow: "hidden",
          backgroundColor: "#0F172A",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          backgroundColor: "#1E293B",
          minHeight: 48,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: "#E2E8F0",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            mr: 1,
          }}
        >
          {fileName}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#94A3B8",
            padding: "6px",
            "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          padding: 2,
          position: "relative",
        }}
      >
        {category === "image" && (
          <img
            src={downloadUrl}
            alt={fileName}
            style={{
              maxWidth: "100%",
              maxHeight: "75vh",
              objectFit: "contain",
              borderRadius: 4,
            }}
          />
        )}
        {category === "video" && (
          <video
            src={downloadUrl}
            controls
            autoPlay={false}
            style={{
              maxWidth: "100%",
              maxHeight: "75vh",
              borderRadius: 4,
              outline: "none",
            }}
          >
            <track kind="captions" />
          </video>
        )}
        {category === "other" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 32px",
              textAlign: "center",
            }}
          >
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 64, color: "#475569", mb: 2 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#E2E8F0", mb: 0.5, wordBreak: "break-all" }}>
              {fileName}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748B", mb: 3 }}>
              This file type cannot be previewed in the browser.
            </Typography>
            <Button
              startIcon={<GetAppIcon />}
              onClick={handleDownload}
              sx={{
                textTransform: "none",
                color: "#fff",
                backgroundColor: "#5A9559",
                borderRadius: "8px",
                fontSize: 13,
                fontWeight: 500,
                px: 2,
                "&:hover": { backgroundColor: "#4A8449" },
              }}
            >
              Download File
            </Button>
          </Box>
        )}
      </Box>

      {/* Footer with download button (for image/video) */}
      {category !== "other" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "8px 16px 12px",
            backgroundColor: "#1E293B",
          }}
        >
          <Button
            startIcon={<GetAppIcon />}
            onClick={handleDownload}
            size="small"
            sx={{
              textTransform: "none",
              color: "#fff",
              backgroundColor: "#5A9559",
              borderRadius: "8px",
              fontSize: 13,
              fontWeight: 500,
              px: 2,
              "&:hover": { backgroundColor: "#4A8449" },
            }}
          >
            Download
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

AttachmentPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fileName: PropTypes.string,
  fileType: PropTypes.string,
  downloadUrl: PropTypes.string,
};

export default AttachmentPreviewDialog;
